import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, TrendingUp, TrendingDown, Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLeaveBalances, useLeavePolicy, usePolicyAssignments, useLeavePolicies } from '@/hooks/leave';
import { format } from 'date-fns';

interface BalanceViewDialogProps {
  policyId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BalanceViewDialog({ policyId, open, onOpenChange }: BalanceViewDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPolicyId, setCurrentPolicyId] = useState<string | null>(policyId);
  
  // Update currentPolicyId when policyId prop changes
  useEffect(() => {
    if (policyId !== currentPolicyId) {
      setCurrentPolicyId(policyId);
    }
  }, [policyId]);
  
  const { data: allPolicies } = useLeavePolicies();
  const { data: policy } = useLeavePolicy(currentPolicyId || '');
  const { data: balances, isLoading } = useLeaveBalances();
  const { data: assignments } = usePolicyAssignments(currentPolicyId || '');

  // Filter balances for this policy and only active assignments
  const filteredBalances = useMemo(() => {
    if (!balances || !policyId || !assignments) return [];
    
    // Get profile IDs of employees with active assignments to this policy
    const activeProfileIds = new Set(assignments.map(assignment => assignment.profile_id));
    
    // Filter balances to only include employees with active assignments
    const policyBalances = balances.filter(balance => 
      balance.policy_id === currentPolicyId && activeProfileIds.has(balance.profile_id)
    );
    
    if (!searchTerm) return policyBalances;
    
    const searchLower = searchTerm.toLowerCase();
    return policyBalances.filter(balance => 
      balance.profile.first_name?.toLowerCase().includes(searchLower) ||
      balance.profile.last_name?.toLowerCase().includes(searchLower) ||
      balance.profile.email.toLowerCase().includes(searchLower)
    );
  }, [balances, policyId, assignments, searchTerm]);

  const getEmployeeDisplayName = (profile: any) => {
    if (profile.first_name || profile.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    return profile.email;
  };

  const formatBalance = (days?: number, hours?: number) => {
    const parts = [];
    if (days && days > 0) parts.push(`${days} days`);
    if (hours && hours > 0) parts.push(`${hours} hours`);
    return parts.length > 0 ? parts.join(', ') : '0';
  };

  const getBalanceStatus = (currentDays: number, usedDays: number) => {
    if (currentDays < 0) return { label: 'Negative', variant: 'destructive' as const };
    if (currentDays <= 2) return { label: 'Low', variant: 'secondary' as const };
    if (usedDays === 0) return { label: 'Unused', variant: 'outline' as const };
    return { label: 'Active', variant: 'default' as const };
  };

  const totalStats = useMemo(() => {
    if (!filteredBalances.length) return null;
    
    return filteredBalances.reduce((acc, balance) => ({
      totalEmployees: acc.totalEmployees + 1,
      totalCurrentDays: acc.totalCurrentDays + balance.current_balance_days,
      totalUsedDays: acc.totalUsedDays + balance.used_days_ytd,
      totalPendingDays: acc.totalPendingDays + balance.pending_days,
    }), {
      totalEmployees: 0,
      totalCurrentDays: 0,
      totalUsedDays: 0,
      totalPendingDays: 0,
    });
  }, [filteredBalances]);

  // Navigation helpers
  const activePolicies = useMemo(() => {
    return allPolicies?.filter(p => p.is_active) || [];
  }, [allPolicies]);

  const currentPolicyIndex = useMemo(() => {
    return activePolicies.findIndex(p => p.id === currentPolicyId);
  }, [activePolicies, currentPolicyId]);

  const canNavigatePrev = currentPolicyIndex > 0;
  const canNavigateNext = currentPolicyIndex < activePolicies.length - 1;

  const handlePrevPolicy = () => {
    if (canNavigatePrev) {
      setCurrentPolicyId(activePolicies[currentPolicyIndex - 1].id);
    }
  };

  const handleNextPolicy = () => {
    if (canNavigateNext) {
      setCurrentPolicyId(activePolicies[currentPolicyIndex + 1].id);
    }
  };

  const handlePolicySelect = (selectedPolicyId: string) => {
    setCurrentPolicyId(selectedPolicyId);
  };

  if (!currentPolicyId || !policy) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Leave Balances: {policy.name}
            </div>
            
            {/* Policy Navigation */}
            <div className="flex items-center gap-2">
              {/* Previous/Next Buttons */}
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPolicy}
                  disabled={!canNavigatePrev}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPolicy}
                  disabled={!canNavigateNext}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Policy Selector */}
              <Select value={currentPolicyId} onValueChange={handlePolicySelect}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select policy" />
                </SelectTrigger>
                <SelectContent>
                  {activePolicies.map((policy) => (
                    <SelectItem key={policy.id} value={policy.id}>
                      {policy.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </DialogTitle>
          <DialogDescription>
            View and monitor leave balances for employees currently assigned to this policy.
            {activePolicies.length > 1 && (
              <span className="ml-1">({currentPolicyIndex + 1} of {activePolicies.length} policies)</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4 min-h-0">
          {/* Search */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search employees by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Summary Stats */}
          {totalStats && (
            <div className="grid grid-cols-4 gap-4 flex-shrink-0">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Total Employees</p>
                      <p className="text-lg font-semibold">{totalStats.totalEmployees}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Available Days</p>
                      <p className="text-lg font-semibold">{totalStats.totalCurrentDays.toFixed(1)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-orange-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Used Days</p>
                      <p className="text-lg font-semibold">{totalStats.totalUsedDays.toFixed(1)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Days</p>
                      <p className="text-lg font-semibold">{totalStats.totalPendingDays.toFixed(1)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            )}

          {/* Balances Table */}
          <div className="flex-1 min-h-0">
            <ScrollArea className="h-full border rounded-md">
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading balances...
                </div>
              ) : filteredBalances.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm ? 'No active employees found matching your search.' : 'No employees currently assigned to this policy.'}
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Current Balance</TableHead>
                    <TableHead>Annual Entitlement</TableHead>
                    <TableHead>Used This Year</TableHead>
                    <TableHead>Pending Requests</TableHead>
                    <TableHead>Carried Over</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBalances.map((balance) => {
                    const status = getBalanceStatus(balance.current_balance_days, balance.used_days_ytd);
                    
                    return (
                      <TableRow key={balance.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {getEmployeeDisplayName(balance.profile)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {balance.profile.email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-semibold">
                              {formatBalance(balance.current_balance_days, balance.current_balance_hours)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatBalance(balance.annual_entitlement_days, balance.annual_entitlement_hours)}
                        </TableCell>
                        <TableCell>
                          {formatBalance(balance.used_days_ytd, balance.used_hours_ytd)}
                        </TableCell>
                        <TableCell>
                          {balance.pending_days > 0 || balance.pending_hours > 0 ? (
                            <span className="text-yellow-600">
                              {formatBalance(balance.pending_days, balance.pending_hours)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {balance.carried_over_days > 0 || balance.carried_over_hours > 0 ? (
                            formatBalance(balance.carried_over_days, balance.carried_over_hours)
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(balance.last_calculated_at), 'MMM d, yyyy')}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </div>

          {/* Actions */}
          <div className="flex justify-end pt-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}